import { useEffect, useMemo, useState } from "react";
import { useI18n } from "../lib/i18n";
import { MyServicesPanel } from "../components/Membership";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";
import api from "../lib/api";

/**
 * Feb 2026 · 20 Jun 2026 · Kunden-„Leistungsübersicht" (read-only).
 *
 * User-Directive (20 Jun 2026): Reine Übersicht — die Auswahl-Liste
 * („Leistungen auswählen") und die „Ausgewählt · Weiter"-Leiste wurden
 * entfernt. Zusatzleistungen bucht der Kunde wie gewohnt über „Ab-/Anreise".
 * Diese Seite zeigt nur noch die Mitgliedschafts-Leistungen („Meine
 * Leistungen") und eine read-only Übersicht aller buchbaren
 * Zusatzleistungen (Modal).
 */
export default function CustomerCatalogue() {
  const { t, lang } = useI18n();
  const de = lang === "de";
  const [extras, setExtras] = useState([]);
  const [overviewOpen, setOverviewOpen] = useState(false); // Lese-Übersicht Modal

  useEffect(() => {
    api.get("/extras").then((r) => setExtras(r.data)).catch((e) => console.error("extras load failed", e));
  }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const s of extras) (g[s.category] = g[s.category] || []).push(s);
    return g;
  }, [extras]);

  return (
    <div className="max-w-[1000px] mx-auto px-5 md:px-10 py-10 md:py-14 pb-40" data-testid="customer-catalogue">
      <div className="mb-8">
        <div className="label-caps mb-3">Weissblom</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">
          {de ? "Leistungsübersicht" : "Services overview"}
        </h1>
        <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">
          {de
            ? "Ihre Mitgliedschafts-Leistungen sowie alle buchbaren Zusatzleistungen auf einen Blick. Zusatzleistungen buchen Sie bequem über „Ab-/Anreise“ — Ihr Concierge-Team koordiniert alles Weitere."
            : "Your membership services plus all bookable add-on services at a glance. Book add-ons conveniently via “Absence” — your concierge team coordinates the rest."}
        </p>
      </div>

      <section className="mb-10 max-w-2xl" data-testid="my-services-section">
        <MyServicesPanel />
      </section>

      <section className="mb-10 max-w-2xl" data-testid="extras-overview-section">
        <button
          type="button"
          data-testid="extras-overview-button"
          onClick={() => setOverviewOpen(true)}
          className="w-full bg-white border border-navy/10 hover:border-gold transition-colors p-6 flex items-center justify-between group text-left"
        >
          <div>
            <div className="label-caps mb-1" style={{ color: BRAND_COLORS.gold }}>{de ? "Maßgeschneidert angepasst" : "Tailored to you"}</div>
            <div className="font-display text-2xl text-navy leading-tight">{de ? "Buchbare Zusatzleistungen während Ihrer Abwesenheit" : "Bookable add-on services during your absence"}</div>
            <p className="text-[12px] text-navy-dark/60 mt-1">
              {de ? "Leistungen, die sich individuell an Ihren zusätzlichen Wünschen orientieren" : "Services tailored individually to your additional wishes"}
            </p>
          </div>
          <span className="text-xl transition-transform group-hover:translate-x-1" style={{ color: BRAND_COLORS.gold }}>→</span>
        </button>
      </section>

      {overviewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: withAlpha(BRAND_COLORS.navyDark, 0.75) }}
          data-testid="extras-overview-modal"
          onClick={() => setOverviewOpen(false)}
        >
          <div
            className="bg-ivory max-w-2xl w-full max-h-[85vh] overflow-y-auto border border-gold/40"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 border-b border-navy/10 flex items-start justify-between gap-4 sticky top-0 bg-ivory z-10">
              <div>
                <div className="label-caps mb-2" style={{ color: BRAND_COLORS.gold }}>Weissblom</div>
                <h2 className="font-display text-3xl text-navy">{de ? "Übersicht Zusatzleistungen" : "Add-on services overview"}</h2>
                <p className="text-[13px] text-navy-dark/70 mt-2 leading-relaxed max-w-md">
                  {de
                    ? "Alle Zusatzleistungen, die Sie zu einer Abwesenheit hinzubuchen können — zum Nachlesen."
                    : "All add-on services you can book alongside an absence — for reference."}
                </p>
                <p className="text-[13px] text-navy-dark/70 mt-2 leading-relaxed max-w-md">
                  {de ? (
                    <>Gewünschte Zusatzleistungen sind mit Ihrer Buchung im Bereich <b>Meine Abwesenheit vorbereiten</b> wählbar.</>
                  ) : (
                    <>Desired add-on services can be selected with your booking in the <b>Prepare my absence</b> section.</>
                  )}
                </p>
              </div>
              <button
                type="button"
                data-testid="extras-overview-close"
                onClick={() => setOverviewOpen(false)}
                className="label-caps font-bold border rounded px-3 py-1.5 transition-colors hover:bg-[#1F4B7A]/10"
                style={{ color: "#1F4B7A", borderColor: "#1F4B7A", fontWeight: 700 }}
              >
                {de ? "Schließen" : "Close"}
              </button>
            </div>
            <div className="p-8 space-y-8" data-testid="extras-overview-groups">
              {Object.entries(grouped).map(([cat, list]) => (
                <section key={cat} data-testid={`overview-group-${cat}`}>
                  <div className="flex items-baseline gap-4 mb-4">
                    <h3 className="font-display text-xl text-navy">{t.catalogue.categories[cat] || cat}</h3>
                    <div className="flex-1 border-b border-gold/40" />
                    <span className="label-caps text-navy-dark/50">{list.length}</span>
                  </div>
                  <ul className="space-y-3">
                    {list.map((s) => (
                      <li key={s.id} className="flex items-start gap-4 pb-3 border-b border-navy/10 last:border-b-0">
                        <span className="text-[14px] text-navy-dark leading-relaxed">{lang === "en" ? s.name_en : s.name_de}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
